import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Mic, MicOff, Phone, PhoneOff, Copy, Download, RotateCcw, Bot, Loader2, Check, AlertTriangle, Route } from 'lucide-react';

import { aiApi } from '../../../api/ai.js';
import { useAuth } from '../../../auth/AuthProvider.jsx';
import { Card } from '../../../components/ui/Card.jsx';
import { PageHeader } from '../../../components/ui/PageHeader.jsx';
import { Button } from '../../../components/ui/Button.jsx';
import { Badge } from '../../../components/ui/Badge.jsx';
import { Tabs } from '../../../components/ui/Tabs.jsx';
import { Composer } from '../../../components/ai/Composer.jsx';
import { MessageBubble } from '../../../components/ai/MessageBubble.jsx';
import { ModelControls } from '../../../components/ai/ModelControls.jsx';
import { MetricsBar } from '../../../components/ai/MetricsBar.jsx';
import { VoiceOrb } from '../../../components/ai/VoiceOrb.jsx';
import { TranscriptPanel } from '../../../components/ai/TranscriptPanel.jsx';
import { useRealtimeVoice } from '../../../hooks/useRealtimeVoice.js';
import { t } from '../../../lib/i18n.js';
import { toolLabel } from '../../../lib/toolLabels.js';

// Capability chips shown under an agent reply (the internal routeToAgent
// handoff is intentionally not a capability and is rendered as the agent chip).
function uniqueToolLabels(names) {
  const seen = [];
  for (const n of names || []) {
    const label = toolLabel(n);
    if (label && !seen.includes(label)) seen.push(label);
  }
  return seen;
}

/**
 * Live pipeline log shown while an agent turn runs: routing, the answering
 * agent (and handoff), and each tool as it starts/finishes.
 */
function ActivityLog({ items, running }) {
  if (!items || items.length === 0) {
    return running ? (
      <div className="mb-2 flex items-center gap-2 pl-11 text-[11px] text-ink-muted">
        <Loader2 className="h-3 w-3 animate-spin" /> Avvio…
      </div>
    ) : null;
  }
  return (
    <ul className="mb-2 flex flex-col gap-1 pl-11">
      {items.map((it, i) => {
        let icon;
        let label;
        if (it.kind === 'stage') { icon = <Route className="h-3 w-3 text-info" />; label = it.label; }
        else if (it.kind === 'agent') { icon = <Bot className="h-3 w-3 text-accent" />; label = it.label; }
        else {
          label = toolLabel(it.name);
          if (it.status === 'running') icon = <Loader2 className="h-3 w-3 animate-spin text-ink-muted" />;
          else if (it.status === 'error') icon = <AlertTriangle className="h-3 w-3 text-danger" />;
          else icon = <Check className="h-3 w-3 text-success" />;
        }
        return (
          <li key={i} className="flex items-center gap-1.5 text-[11px] text-ink-muted">
            {icon}
            <span>{label}</span>
          </li>
        );
      })}
    </ul>
  );
}

const EMPTY_CTRL = {
  personalityId: '',
  model: '',
  realtimeModel: '',
  voice: '',
  agentId: '',
  temperature: '0.7',
  maxTokens: '',
  streaming: true
};

function transcriptToText(turns) {
  return turns
    .map((m) => `${m.role === 'user' ? 'Utente' : 'Assistente'}: ${m.content ?? m.text ?? ''}`)
    .join('\n\n');
}

export function PlaygroundPage() {
  const { hasPermission } = useAuth();
  const canTest = hasPermission('ai:test');

  const [mode, setMode] = useState('text');
  const [ctrl, setCtrl] = useState(EMPTY_CTRL);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [lastUsage, setLastUsage] = useState(null);
  const [streamError, setStreamError] = useState(null);
  const listRef = useRef(null);

  const voice = useRealtimeVoice();

  const optionsQuery = useQuery({
    queryKey: ['ai', 'playground', 'options'],
    enabled: canTest,
    queryFn: () => aiApi.playgroundOptions()
  });
  const options = optionsQuery.data || { chatModels: [], realtimeModels: [], voices: [], agents: [], defaults: {}, voiceEnabled: false };

  const personalitiesQuery = useQuery({
    queryKey: ['ai', 'personalities', 'picker'],
    enabled: canTest,
    queryFn: () => aiApi.listPersonalities({ limit: 100 })
  });

  const personalityOptions = useMemo(() => {
    const items = (personalitiesQuery.data && personalitiesQuery.data.items) || [];
    return [
      { value: '', label: 'Personalità attiva (default)' },
      ...items.map((p) => ({ value: p._id, label: `${p.name}${p.isActive ? ' (attiva)' : ''}` }))
    ];
  }, [personalitiesQuery.data]);

  // Seed model/voice/agent defaults once options arrive. The agent defaults to
  // the orchestrator (both text and voice) so the playground routes turns
  // through it out of the box; fall back to the first enabled agent.
  useEffect(() => {
    if (!optionsQuery.data) return;
    const agents = options.agents || [];
    const defaultAgentId = agents.some((a) => a.id === 'orchestrator')
      ? 'orchestrator'
      : (agents[0] ? agents[0].id : '');
    setCtrl((prev) => ({
      ...prev,
      model: prev.model || options.defaults.model || (options.chatModels[0] || ''),
      realtimeModel: prev.realtimeModel || options.defaults.realtimeModel || (options.realtimeModels[0] || ''),
      voice: prev.voice || options.defaults.voice || (options.voices[0] || ''),
      agentId: prev.agentId || defaultAgentId
    }));
  }, [optionsQuery.data]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, sending]);

  const onCtrlChange = (key, value) => {
    // The model selector binds to a different field per mode.
    const realKey = key === 'model' && mode === 'voice' ? 'realtimeModel' : key;
    setCtrl((prev) => ({ ...prev, [realKey]: value }));
  };

  const controlState = { ...ctrl, model: mode === 'voice' ? ctrl.realtimeModel : ctrl.model };

  const send = async (overrideText) => {
    const text = (typeof overrideText === 'string' ? overrideText : draft).trim();
    if (!text || sending) return;
    const history = [...messages, { role: 'user', content: text }];
    setMessages(history);
    if (typeof overrideText !== 'string') setDraft('');
    setSending(true);
    setStreamError(null);

    // Agent mode: route the turn through the multi agent orchestrator (handoff
    // + tools). When streaming is on we surface the live pipeline (routing,
    // answering agent, each tool, then the answer deltas).
    if (ctrl.agentId) {
      const agentBody = {
        messages: history.map((m) => ({ role: m.role, content: m.content })),
        agentId: ctrl.agentId,
        ...(ctrl.personalityId ? { personalityId: ctrl.personalityId } : {}),
        ...(ctrl.model ? { model: ctrl.model } : {}),
        temperature: Number(ctrl.temperature) || 0.7
      };

      const dropFailedTurn = () => {
        setMessages((prev) => {
          const next = [...prev];
          if (next.length && next[next.length - 1].role === 'assistant' && next[next.length - 1].pending) next.pop();
          if (next.length && next[next.length - 1].role === 'user') next.pop();
          return next;
        });
        setDraft(text);
      };

      if (ctrl.streaming) {
        // Placeholder assistant message we mutate as events arrive.
        setMessages((prev) => [...prev, { role: 'assistant', content: '', streaming: true, pending: true, agentLabel: null, activity: [], tools: [] }]);
        const patchLast = (patch) => setMessages((prev) => {
          const next = [...prev];
          const last = next[next.length - 1];
          if (last && last.role === 'assistant') next[next.length - 1] = { ...last, ...(typeof patch === 'function' ? patch(last) : patch) };
          return next;
        });

        try {
          const final = await aiApi.playgroundAgentStream(agentBody, {
            onEvent: (evt) => {
              if (evt.type === 'stage') {
                patchLast((l) => ({ activity: [...l.activity, { kind: 'stage', label: 'Instradamento…' }] }));
              } else if (evt.type === 'agent') {
                patchLast((l) => ({
                  agentLabel: evt.agent ? evt.agent.label : l.agentLabel,
                  activity: [...l.activity, { kind: 'agent', label: evt.routing ? `Handoff → ${evt.agent.label}` : (evt.agent ? evt.agent.label : 'Agente') }]
                }));
              } else if (evt.type === 'tool') {
                patchLast((l) => {
                  const activity = [...l.activity];
                  if (evt.phase === 'start') {
                    activity.push({ kind: 'tool', name: evt.name, status: 'running' });
                  } else {
                    for (let i = activity.length - 1; i >= 0; i -= 1) {
                      if (activity[i].kind === 'tool' && activity[i].name === evt.name && activity[i].status === 'running') {
                        activity[i] = { ...activity[i], status: evt.errored ? 'error' : 'done' };
                        break;
                      }
                    }
                  }
                  return { activity };
                });
              } else if (evt.type === 'delta') {
                patchLast((l) => ({ content: l.content + evt.text }));
              }
            }
          });
          patchLast((l) => ({
            streaming: false,
            pending: false,
            content: final && typeof final.reply === 'string' ? final.reply : l.content,
            suggestions: final ? (final.suggestions || []) : [],
            widgets: final && Array.isArray(final.widgets) ? final.widgets : [],
            tools: final ? (final.toolTrace || []).map((tr) => tr.name) : l.tools
          }));
          if (final) {
            setLastUsage({
              model: ctrl.model || undefined,
              usage: { totalTokens: final.usage ? (final.usage.tokensIn + final.usage.tokensOut) : 0 },
              costUsd: final.usage ? final.usage.costUsd : 0,
              finishReason: final.finishReason
            });
          }
        } catch (err) {
          const message = (err && err.message) ? err.message : t('common.error');
          toast.error(message);
          setStreamError(message);
          dropFailedTurn();
        } finally {
          setSending(false);
        }
        return;
      }

      // Non-streaming agent path (kept for when streaming is off).
      try {
        const data = await aiApi.playgroundAgent(agentBody);
        setMessages((prev) => [...prev, {
          role: 'assistant',
          content: data.reply || '',
          agentLabel: data.agent ? data.agent.label : null,
          suggestions: data.suggestions || [],
          widgets: Array.isArray(data.widgets) ? data.widgets : [],
          tools: Array.isArray(data.toolTrace) ? data.toolTrace.map((tr) => tr.name) : []
        }]);
        setLastUsage({
          model: ctrl.model || undefined,
          usage: { totalTokens: data.usage ? (data.usage.tokensIn + data.usage.tokensOut) : 0 },
          costUsd: data.usage ? data.usage.costUsd : 0,
          finishReason: data.routing ? `handoff → ${data.agent.label}` : (data.agent ? data.agent.label : null)
        });
      } catch (err) {
        const message = (err && err.message) ? err.message : t('common.error');
        toast.error(message);
        setStreamError(message);
        setMessages((prev) => {
          const next = [...prev];
          if (next.length && next[next.length - 1].role === 'user') next.pop();
          return next;
        });
        setDraft(text);
      } finally {
        setSending(false);
      }
      return;
    }

    const payload = {
      messages: history.map((m) => ({ role: m.role, content: m.content })),
      temperature: Number(ctrl.temperature) || 0.7
    };
    if (ctrl.personalityId) payload.personalityId = ctrl.personalityId;
    if (ctrl.model) payload.model = ctrl.model;
    const mt = Number(ctrl.maxTokens);
    if (Number.isFinite(mt) && mt > 0) payload.maxTokens = mt;

    try {
      if (ctrl.streaming) {
        setMessages((prev) => [...prev, { role: 'assistant', content: '', streaming: true }]);
        const final = await aiApi.playgroundStream(payload, {
          onDelta: (chunk) => setMessages((prev) => {
            const next = [...prev];
            const last = next[next.length - 1];
            if (last && last.role === 'assistant') next[next.length - 1] = { ...last, content: last.content + chunk };
            return next;
          })
        });
        setMessages((prev) => {
          const next = [...prev];
          const last = next[next.length - 1];
          if (last && last.role === 'assistant') {
            next[next.length - 1] = {
              ...last,
              streaming: false,
              content: final && typeof final.reply === 'string' ? final.reply : last.content,
              suggestions: final ? (final.suggestions || []) : []
            };
          }
          return next;
        });
        if (final) setLastUsage(final);
      } else {
        const data = await aiApi.playground(payload);
        const reply = typeof data.reply === 'string' ? data.reply : (data.reply && data.reply.content) || '';
        setMessages((prev) => [...prev, { role: 'assistant', content: reply, suggestions: data.suggestions || [] }]);
        setLastUsage(data);
      }
    } catch (err) {
      // The streaming client throws a plain Error carrying the provider
      // message (e.g. an unsupported model), so surface err.message for both
      // ApiError and stream errors instead of a generic fallback.
      const message = (err && err.message) ? err.message : t('common.error');
      toast.error(message);
      setStreamError(message);
      // Drop the failed turn(s) so the user can retry.
      setMessages((prev) => {
        const next = [...prev];
        if (next.length && next[next.length - 1].role === 'assistant' && !next[next.length - 1].content) next.pop();
        if (next.length && next[next.length - 1].role === 'user') next.pop();
        return next;
      });
      setDraft(text);
    } finally {
      setSending(false);
    }
  };

  const reset = () => {
    setMessages([]);
    setLastUsage(null);
    setStreamError(null);
  };

  const startVoice = () => {
    voice.start({
      agent: ctrl.agentId || undefined,
      model: ctrl.realtimeModel || undefined,
      voice: ctrl.voice || undefined,
      personalityId: ctrl.personalityId || undefined,
      // The playground is an ephemeral testing surface: do not persist the
      // session as a conversation in the operator's history.
      playground: 1
    });
  };

  const copyTranscript = async () => {
    const turns = mode === 'voice' ? voice.transcript : messages;
    if (!turns.length) return;
    try { await navigator.clipboard.writeText(transcriptToText(turns)); toast.success('Trascrizione copiata.'); }
    catch { toast.error('Copia non riuscita.'); }
  };

  const exportTranscript = () => {
    const turns = mode === 'voice' ? voice.transcript : messages;
    if (!turns.length) return;
    const blob = new Blob([transcriptToText(turns)], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `playground-${mode}-transcript.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!canTest) {
    return (
      <div className="flex flex-col gap-6">
        <PageHeader title={t('nav.aiPlayground')} />
        <Card>
          <p className="text-sm text-ink-muted">
            Non hai il permesso <code className="rounded bg-border/40 px-1 py-0.5 text-xs">ai:test</code> per usare il playground.
          </p>
        </Card>
      </div>
    );
  }

  const voiceLive = voice.status === 'live' || voice.status === 'connecting';
  const hasTurns = (mode === 'voice' ? voice.transcript.length : messages.length) > 0;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={t('nav.aiPlayground')}
        description="Prova testo e voce con la stessa pipeline di kiosk e dipendenti."
        actions={
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={copyTranscript} disabled={!hasTurns}><Copy className="h-4 w-4" /> Copia</Button>
            <Button variant="ghost" size="sm" onClick={exportTranscript} disabled={!hasTurns}><Download className="h-4 w-4" /> Esporta</Button>
            {mode === 'text' && (
              <Button variant="ghost" size="sm" onClick={reset} disabled={!messages.length}><RotateCcw className="h-4 w-4" /> Pulisci</Button>
            )}
          </div>
        }
      />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[280px_1fr]">
        <Card className="h-fit">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-ink">Parametri</h2>
            <Tabs
              value={mode}
              onChange={setMode}
              items={[
                { value: 'text', label: 'Testo' },
                { value: 'voice', label: 'Voce' }
              ]}
            />
          </div>

          {mode === 'voice' && !options.voiceEnabled ? (
            <p className="rounded-md border border-warning/20 bg-warning/5 px-3 py-2 text-xs text-warning">
              Il broker vocale è disattivato (impostazione <code>voice.enabled</code>).
            </p>
          ) : (
            <ModelControls
              mode={mode}
              options={options}
              personalityOptions={personalityOptions}
              state={controlState}
              onChange={onCtrlChange}
              disabled={mode === 'voice' && voiceLive}
            />
          )}

          {mode === 'text' && <div className="mt-5"><MetricsBar lastUsage={lastUsage} /></div>}

          {mode === 'voice' && voice.meta && voice.meta.budget && (
            <div className="mt-5 border-t border-border pt-4 text-xs text-ink-muted">
              Budget vocale: {voice.meta.budget.allowed ? <Badge tone="success">attivo</Badge> : <Badge tone="danger">esaurito</Badge>}
              {typeof voice.meta.budget.remainingSeconds === 'number' && (
                <span className="ml-2">~{Math.floor(voice.meta.budget.remainingSeconds / 60)} min residui</span>
              )}
            </div>
          )}
        </Card>

        <Card className="flex h-[70vh] flex-col p-0">
          {mode === 'text' ? (
            <>
              <div ref={listRef} className="flex-1 overflow-y-auto p-5">
                {messages.length === 0 ? (
                  <div className="flex h-full items-center justify-center">
                    <p className="max-w-sm text-center text-sm text-ink-muted">
                      Invia o detta un messaggio per testare la personalità. Attiva lo streaming per la risposta in tempo reale.
                    </p>
                  </div>
                ) : (
                  <ul className="flex flex-col gap-4">
                    {messages.map((m, idx) => {
                      const tools = m.role === 'assistant' ? uniqueToolLabels(m.tools) : [];
                      const agentLabel = m.role === 'assistant' ? m.agentLabel : null;
                      return (
                        <li key={idx}>
                          {m.role === 'assistant' && (m.activity?.length > 0 || (m.streaming && !m.content)) && (
                            <ActivityLog items={m.activity} running={m.streaming} />
                          )}
                          <MessageBubble role={m.role} content={m.content} streaming={m.streaming} suggestions={idx === messages.length - 1 ? m.suggestions : undefined} widgets={m.widgets} onSuggestion={send} />
                          {(agentLabel || tools.length > 0) && (
                            <div className="mt-1.5 flex flex-wrap items-center gap-2 pl-11">
                              {agentLabel && (
                                <span className="inline-flex items-center gap-1 rounded-full border border-accent/30 bg-accent-soft px-2 py-0.5 text-[11px] font-medium text-accent">
                                  <Bot className="h-3 w-3" strokeWidth={2} />
                                  {agentLabel}
                                </span>
                              )}
                              {tools.map((label) => (
                                <span key={label} className="inline-flex items-center rounded-full border border-border bg-surface-subtle px-2 py-0.5 text-[11px] text-ink-muted">
                                  {label}
                                </span>
                              ))}
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
              <div className="border-t border-border p-3">
                {streamError && (
                  <p className="mb-2 rounded-md border border-danger/20 bg-danger/5 px-3 py-2 text-xs text-danger">
                    {streamError}
                  </p>
                )}
                <Composer value={draft} onChange={setDraft} onSubmit={send} sending={sending} />
              </div>
            </>
          ) : (
            <>
              <div className="flex flex-col items-center justify-center gap-4 border-b border-border py-6">
                <VoiceOrb status={voice.status} speaking={voice.speaking} userSpeaking={voice.userSpeaking} />
                <div className="flex items-center gap-2">
                  {voiceLive ? (
                    <>
                      <Button variant="secondary" size="sm" onClick={voice.toggleMute}>
                        {voice.muted ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
                        {voice.muted ? 'Microfono off' : 'Microfono on'}
                      </Button>
                      <Button variant="danger" size="sm" onClick={voice.stop}>
                        <PhoneOff className="h-4 w-4" /> Termina
                      </Button>
                    </>
                  ) : (
                    <Button size="sm" onClick={startVoice} disabled={!options.voiceEnabled}>
                      <Phone className="h-4 w-4" /> Avvia conversazione
                    </Button>
                  )}
                </div>
                {voice.error && <p className="text-xs text-danger">{voice.error}</p>}
              </div>
              <TranscriptPanel transcript={voice.transcript} />
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
